import { useEffect, useState } from 'react'
import { Grid, Card, Box, Typography } from '@mui/material'
import { Page, SearchButton } from 'src/admin/core/components'
import { useAppTranslation, useNotification, useUserClaimActions } from 'src/admin/core/hooks'
import { Filter as FilterModel, IssuedTickets } from './models'
import Filters, { initialFilters } from './components/Filters'

import { generateFilterGraphql } from 'src/admin/core'
import { formatToShortAPItDate } from 'src/admin/core/services/dateFunctions'
import { ClaimActions } from 'src/admin/core/models'
import { useIssuedTicketBySequence } from './hooks/useIssuedTicketBySequence'
import { CanceledPaidAlert } from './components/CanceledPaidAlert'
import TicketReceipt from './components/TicketReceipt'
import LotteryTicketReceipt from './components/LotteryTicketReceipt'
import { getAppBarState } from 'src/store'
import { inquiryTicket, TicketSaleResponse, TicketSaleTransaction } from 'src/admin/core/services/lottery/inquiryTicket'

/**
 * Validates if the lottery ticket belongs to the selected agency, workstation and matches the amount
 * All three conditions must be met:
 * 1. Agency must be selected and match ticketDetails.agencyId
 * 2. Workstation must be selected and match ticketDetails.workstationId
 * 3. Amount must be entered and match ticketDetails.amount
 */
const validateLotteryTicket = (response: TicketSaleResponse, filters: FilterModel): boolean => {
  const ticketDetails = response.ticketDetails

  if (!ticketDetails) {
    return false
  }

  // Validate agency - REQUIRED
  if (!filters.locationId || ticketDetails.agencyId !== filters.locationId) {
    return false
  }

  // Validate workstation - REQUIRED
  if (!filters.stationId || ticketDetails.workstationId !== filters.stationId) {
    return false
  }

  // Validate amount - REQUIRED
  if (filters.wagerAmountTotal === undefined || ticketDetails.amount !== filters.wagerAmountTotal) {
    return false
  }

  return true
}

const TicketVoid = () => {
  const { label } = useAppTranslation()
  const { issuedTickets, count, loading, getIssuedTickets, error } = useIssuedTicketBySequence()
  const [currentFilters, setCurrentFilters] = useState<FilterModel>(initialFilters)
  const { validateClaimActions } = useUserClaimActions()
  const actions = validateClaimActions('consults.tickets-verification', [
    ClaimActions.CanView,
    ClaimActions.CanCreate,
    ClaimActions.CanDelete,
  ])
  const [openAlert, setOpenAlert] = useState(false)
  const [code, setCode] = useState('')
  const [selectedTicket, setSelectedTicket] = useState<IssuedTickets | null>(null)
  const [hasSubmitted, setHasSubmitted] = useState(false)
  const { errorGraphNotification, errorNotification } = useNotification()
  const [ticketNumber, setTicketNumber] = useState('')

  // Lottery ticket state
  const [lotteryResponse, setLotteryResponse] = useState<TicketSaleResponse | null>(null)
  const [lotteryLoading, setLotteryLoading] = useState(false)
  const [lotteryError, setLotteryError] = useState<string | null>(null)
  const [isLotteryTicketValid, setIsLotteryTicketValid] = useState(false)

  const { user } = getAppBarState().appState

  useEffect(() => {
    if (error) {
      errorGraphNotification(error)
    }
  }, [error])

  useEffect(() => {
    if (lotteryError) {
      errorNotification(lotteryError)
    }
  }, [lotteryError])

  const handleLotterySearch = async () => {
    if (!currentFilters.ticketNumber) {
      return
    }

    setLotteryLoading(true)
    setLotteryError(null)
    setLotteryResponse(null)
    setIsLotteryTicketValid(false)

    try {
      const response = await inquiryTicket(currentFilters.ticketNumber)
      setLotteryResponse(response)

      // Validate the ticket against selected filters
      const isValid = validateLotteryTicket(response, currentFilters)
      setIsLotteryTicketValid(isValid)
    } catch (err: any) {
      setLotteryError(err.message || 'Error al consultar el ticket de lotería')
    } finally {
      setLotteryLoading(false)
    }
  }

  const handleSearch = () => {
    setHasSubmitted(false)
    setLotteryResponse(null)
    setLotteryError(null)
    setIsLotteryTicketValid(false)

    // If product is LOTTERY_TICKET, use the lottery API
    if (currentFilters.productId === 'LOTTERY_TICKET') {
      if (currentFilters.ticketNumber !== '') {
        setHasSubmitted(true)
        setTicketNumber(currentFilters.ticketNumber)
        handleLotterySearch()
      }
      return
    }

    // Original logic for other products
    if (
      currentFilters.salesTime &&
      currentFilters.locationId &&
      currentFilters.stationId &&
      currentFilters.productId !== '' &&
      currentFilters.ticketNumber !== '' &&
      currentFilters.wagerAmountTotal !== undefined
    ) {
      setHasSubmitted(true)
      setTicketNumber(currentFilters.ticketNumber)

      getIssuedTickets({
        date: formatToShortAPItDate(currentFilters.salesTime),
        partialTicketNumber: currentFilters.ticketNumber,
        agencyId: String(currentFilters.locationId),
        workstationId: currentFilters.stationId,
        product: String(currentFilters.productId),
        amount: currentFilters.wagerAmountTotal,
        username: user.profile.preferred_username,
        userFullName: user.profile.name,
      })
    }
  }

  const handleCloseAlert = () => {
    setOpenAlert(false)
    setCode('')
    setSelectedTicket(null)
  }

  const ticket = issuedTickets.length > 0 ? issuedTickets[0] : null
  const isTicketTotalAmountValid = ticket?.totalAmount + ticket?.surcharge === currentFilters.wagerAmountTotal
  const isLotteryProduct = currentFilters.productId === 'LOTTERY_TICKET'

  // Get lottery ticket data for rendering
  const lotteryTransaction = lotteryResponse?.datos_trans
    ? Array.isArray(lotteryResponse.datos_trans)
      ? lotteryResponse.datos_trans[0]
      : lotteryResponse.datos_trans
    : null
  const lotteryTicket = lotteryResponse?.tickets?.[0] || null
  const lotteryTicketDetails = lotteryResponse?.ticketDetails || null

  return (
    <Page
      hasAccess={actions.canView}
      tabTitle={label('VERIFICACION_DE_TICKET_TITULO')}
      pageTitle={label(`VERIFICACION_DE_TICKET_TITULO`)}
      pageSubTitle={label('VERIFICACION_DE_TICKET_VOID_SUB_TITULO')}
      actions={<SearchButton color="primary" onClick={handleSearch} isLoading={loading || lotteryLoading} />}
    >
      <Grid container spacing={3}>
        <Grid item xs={7}>
          <Filters onChange={(filters: FilterModel) => setCurrentFilters(filters)} />
        </Grid>
        {hasSubmitted && (
          <Grid item xs={5}>
            {isLotteryProduct ? (
              // Lottery ticket display
              lotteryTicketDetails ? (
                <Box>
                  {!isLotteryTicketValid && (
                    <Box
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                      mb={2}
                      sx={{
                        p: 2,
                        backgroundColor: 'error.light',
                        borderRadius: 1,
                      }}
                    >
                      <Typography variant="body1" color="error.contrastText" fontWeight="bold">
                        Ticket no existe o no coincide con los filtros seleccionados
                      </Typography>
                    </Box>
                  )}
                  {isLotteryTicketValid && (
                    <LotteryTicketReceipt
                      transaction={lotteryTransaction}
                      ticket={lotteryTicket}
                      ticketDetails={lotteryTicketDetails}
                      ticketStatus={lotteryResponse.ticket_status}
                      totalPagar={lotteryResponse.total_pagar}
                      mensaje={lotteryResponse.mensaje}
                      codigo={lotteryResponse.codigo}
                    />
                  )}
                </Box>
              ) : (
                <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                  <Typography variant="body1" color="text.error">
                    {label('CT099_TICKET_NOT_FOUND')}
                  </Typography>
                </Box>
              )
            ) : // Original ticket display
              ticket && isTicketTotalAmountValid ? (
                <TicketReceipt
                  ticket={ticket}
                  ticketNumber={ticketNumber}
                  canCancel={actions.canDelete}
                  canPaid={actions.canCreate}
                />
              ) : !ticket || !isTicketTotalAmountValid ? (
                <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                  <Typography variant="body1" color="text.error">
                    {label('CT099_TICKET_NOT_FOUND')}
                  </Typography>
                </Box>
              ) : (
                <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                  <Typography variant="body1" color="text.error">
                    {count > 1 ? label('CT098_MORE_THAN_ONCE') : ''}
                  </Typography>
                </Box>
              )}
          </Grid>
        )}
      </Grid>
      {ticket && openAlert && (
        <CanceledPaidAlert ticket={ticket} handleClose={handleCloseAlert} open={openAlert} code={code} />
      )}
    </Page>
  )
}

export default TicketVoid
